/**
 * Geometry in Photo adjust (Huanrui, 2026-10-30)
 * -------------------------------------------------------------
 * Camera Raw's Geometry sliders and its Guided Upright, worked out here as
 * one perspective transform (a 3 x 3 matrix) that the engine applies to the
 * picture before anything else (gradeEngine.ts, WARP). Free, in this
 * browser, at full size on Apply.
 *
 * Coordinates: the picture's middle is (0, 0), its longer side is 1 long, y
 * goes down. So the same matrix serves the studio's preview and the full-size
 * picture.
 *
 * In order, a point of the picture is:
 *   1. tilted -- Vertical about the across axis, Horizontal about the up-down
 *      one, at most 30 degrees each, seen from one picture-length away;
 *   2. turned by Rotate (degrees, clockwise);
 *   3. stretched by Aspect (across against up-down);
 *   4. scaled by Scale (%), moved by Offset X / Y (% of half the picture);
 *   5. and the whole made just large enough that no empty corner shows: what
 *      would be empty is cropped away (Huanrui: no switch for it).
 *
 * Guided: lines drawn along what should be upright or level (up to 4). With
 * two or more meant upright, Vertical is worked out; two or more level,
 * Horizontal; Rotate always. They set the sliders, which can be moved after.
 */

export interface GeoSettings {
  geoVertical: number
  geoHorizontal: number
  geoRotate: number
  geoAspect: number
  geoScale: number
  geoOffsetX: number
  geoOffsetY: number
}

/** A guide, in shares (0 to 1) of the picture as it was before any geometry. */
export interface GeoGuide {
  x1: number
  y1: number
  x2: number
  y2: number
}

export const MAX_GUIDES = 4
const MAX_TILT = (30 * Math.PI) / 180

type M3 = [number, number, number, number, number, number, number, number, number]

const mul = (a: M3, b: M3): M3 => [
  a[0] * b[0] + a[1] * b[3] + a[2] * b[6],
  a[0] * b[1] + a[1] * b[4] + a[2] * b[7],
  a[0] * b[2] + a[1] * b[5] + a[2] * b[8],
  a[3] * b[0] + a[4] * b[3] + a[5] * b[6],
  a[3] * b[1] + a[4] * b[4] + a[5] * b[7],
  a[3] * b[2] + a[4] * b[5] + a[5] * b[8],
  a[6] * b[0] + a[7] * b[3] + a[8] * b[6],
  a[6] * b[1] + a[7] * b[4] + a[8] * b[7],
  a[6] * b[2] + a[7] * b[5] + a[8] * b[8],
]

function invert(m: M3): M3 | null {
  const [a, b, c, d, e, f, g, h, i] = m
  const A = e * i - f * h
  const B = -(d * i - f * g)
  const C = d * h - e * g
  const det = a * A + b * B + c * C
  if (Math.abs(det) < 1e-12) return null
  const k = 1 / det
  return [
    A * k, -(b * i - c * h) * k, (b * f - c * e) * k,
    B * k, (a * i - c * g) * k, -(a * f - c * d) * k,
    C * k, -(a * h - b * g) * k, (a * e - b * d) * k,
  ]
}

export function apply(m: M3, x: number, y: number): { x: number; y: number; ok: boolean } {
  const w = m[6] * x + m[7] * y + m[8]
  return { x: (m[0] * x + m[1] * y + m[2]) / w, y: (m[3] * x + m[4] * y + m[5]) / w, ok: w > 1e-6 }
}

/** Nothing moved: the picture as it is. */
export function geoIsIdentity(s: GeoSettings): boolean {
  return (
    s.geoVertical === 0 &&
    s.geoHorizontal === 0 &&
    s.geoRotate === 0 &&
    s.geoAspect === 0 &&
    s.geoScale === 100 &&
    s.geoOffsetX === 0 &&
    s.geoOffsetY === 0
  )
}

/** Steps 1 to 4: the picture's points to where they are shown, before the crop. */
function transformOf(s: GeoSettings, ax: number, ay: number): M3 {
  // 1. Tilted, then seen from one picture-length away (f = 1).
  const tv = (s.geoVertical / 100) * MAX_TILT
  const th = (s.geoHorizontal / 100) * MAX_TILT
  const cv = Math.cos(tv)
  const sv = Math.sin(tv)
  const ch = Math.cos(th)
  const sh = Math.sin(th)
  // R = Ry(th) * Rx(tv), on the picture's plane; its first two columns and the camera.
  const r1 = [ch, 0, sh]
  const r2 = [sh * sv, cv, -ch * sv]
  // Pz = 1 + x * r1z + y * r2z: a positive Vertical brings the top nearer (wider).
  const persp: M3 = [r1[0], r2[0], 0, r1[1], r2[1], 0, -r1[2], -r2[2], 1]
  // 2. Turned.
  const a = (s.geoRotate * Math.PI) / 180
  const rot: M3 = [Math.cos(a), -Math.sin(a), 0, Math.sin(a), Math.cos(a), 0, 0, 0, 1]
  // 3. Stretched: +100 is half as wide again across as up-down.
  const k = Math.pow(1.5, s.geoAspect / 200)
  const asp: M3 = [k, 0, 0, 0, 1 / k, 0, 0, 0, 1]
  // 4. Scaled and moved.
  const z = Math.max(0.1, s.geoScale / 100)
  const move: M3 = [z, 0, (s.geoOffsetX / 100) * (ax / 2), 0, z, (s.geoOffsetY / 100) * (ay / 2), 0, 0, 1]
  return mul(move, mul(asp, mul(rot, persp)))
}

/**
 * How much the shown picture is enlarged so its frame is all picture: the
 * largest share t of the frame that fits inside the transformed picture,
 * which is then enlarged by 1 / t. Never made smaller.
 */
function fillOf(m: M3, ax: number, ay: number): number {
  const hx = ax / 2
  const hy = ay / 2
  const quad = [
    apply(m, -hx, -hy),
    apply(m, hx, -hy),
    apply(m, hx, hy),
    apply(m, -hx, hy),
  ]
  if (quad.some((p) => !p.ok || !Number.isFinite(p.x) || !Number.isFinite(p.y))) return 1
  let area = 0
  for (let i = 0; i < 4; i += 1) {
    const p = quad[i]!
    const q = quad[(i + 1) % 4]!
    area += p.x * q.y - q.x * p.y
  }
  const sign = Math.sign(area) || 1
  const frame = [
    [-hx, -hy],
    [hx, -hy],
    [hx, hy],
    [-hx, hy],
  ]
  let t = Infinity
  for (let i = 0; i < 4; i += 1) {
    const p = quad[i]!
    const q = quad[(i + 1) % 4]!
    const ex = q.x - p.x
    const ey = q.y - p.y
    const B = sign * (ex * p.y - ey * p.x)
    // The middle must be inside; if it is not, the picture is too far gone to crop sensibly.
    if (B > 0) return 1
    for (const [cx, cy] of frame) {
      const A = sign * (ex * cy! - ey * cx!)
      if (A < 0) t = Math.min(t, B / A)
    }
  }
  if (!Number.isFinite(t) || t <= 0) return 1
  return t < 1 ? 1 / t : 1
}

/** Where the picture's points are shown, crop included: picture -> shown. */
export function geoForward(s: GeoSettings, ax: number, ay: number): M3 {
  const m = transformOf(s, ax, ay)
  const z = fillOf(m, ax, ay)
  return mul([z, 0, 0, 0, z, 0, 0, 0, 1], m)
}

/** Which point of the picture each shown point comes from: shown -> picture. What the engine needs. */
export function geoInverse(s: GeoSettings, ax: number, ay: number): M3 {
  return invert(geoForward(s, ax, ay)) ?? [1, 0, 0, 0, 1, 0, 0, 0, 1]
}

/** The matrix as WebGL takes it (column by column). */
export function glMatrix(m: M3): Float32Array {
  return new Float32Array([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]])
}

/* ---------------------------------------------------------------- *
 * Guided
 * ---------------------------------------------------------------- */

/** A guide meant to be upright (more up-down than across as drawn). */
export const isUpright = (g: GeoGuide, ax: number, ay: number) =>
  Math.abs((g.y2 - g.y1) * ay) >= Math.abs((g.x2 - g.x1) * ax)

/**
 * Vertical, Horizontal and Rotate that make the guides upright or level, the
 * other sliders as they are. Small changes are preferred where the guides
 * leave a choice. Nelder-Mead over the three sliders.
 */
export function solveGuides(
  guides: GeoGuide[],
  s: GeoSettings,
  ax: number,
  ay: number,
): Pick<GeoSettings, 'geoVertical' | 'geoHorizontal' | 'geoRotate'> {
  if (guides.length === 0) return { geoVertical: s.geoVertical, geoHorizontal: s.geoHorizontal, geoRotate: s.geoRotate }
  const up = guides.filter((g) => isUpright(g, ax, ay))
  const level = guides.filter((g) => !isUpright(g, ax, ay))
  const freeV = up.length >= 2
  const freeH = level.length >= 2
  const centred = (g: GeoGuide) => ({
    x1: (g.x1 - 0.5) * ax,
    y1: (g.y1 - 0.5) * ay,
    x2: (g.x2 - 0.5) * ax,
    y2: (g.y2 - 0.5) * ay,
  })
  const ups = up.map(centred)
  const levels = level.map(centred)
  const cost = (v: number, h: number, r: number) => {
    const m = transformOf({ ...s, geoVertical: v, geoHorizontal: h, geoRotate: r }, ax, ay)
    let e = 0
    const err = (g: { x1: number; y1: number; x2: number; y2: number }, upright: boolean) => {
      const a = apply(m, g.x1, g.y1)
      const b = apply(m, g.x2, g.y2)
      if (!a.ok || !b.ok) return 10
      const dx = b.x - a.x
      const dy = b.y - a.y
      const len = dx * dx + dy * dy || 1e-9
      return (upright ? dx * dx : dy * dy) / len
    }
    for (const g of ups) e += err(g, true)
    for (const g of levels) e += err(g, false)
    // Out of range costs a lot; any change a little.
    const out = Math.max(0, Math.abs(v) - 100) + Math.max(0, Math.abs(h) - 100) + Math.max(0, Math.abs(r) - 45)
    return e + out * 10 + 1e-10 * (v * v + h * h + r * r)
  }
  const pack = (p: number[]) => [freeV ? p[0]! : s.geoVertical, freeH ? p[1]! : s.geoHorizontal, p[2]!] as const
  const f = (p: number[]) => cost(...pack(p))
  // Nelder-Mead, from the sliders as they are.
  let simplex: number[][] = [[s.geoVertical, s.geoHorizontal, s.geoRotate]]
  for (let i = 0; i < 3; i += 1) {
    const p = [...simplex[0]!]
    p[i] = p[i]! + (i === 2 ? 5 : 20)
    simplex.push(p)
  }
  let values = simplex.map(f)
  for (let step = 0; step < 400; step += 1) {
    const order = values.map((v, i) => [v, i] as const).sort((a, b) => a[0] - b[0]).map(([, i]) => i)
    simplex = order.map((i) => simplex[i]!)
    values = order.map((i) => values[i]!)
    if (Math.abs(values[3]! - values[0]!) < 1e-12) break
    const centroid = [0, 1, 2].map((k) => (simplex[0]![k]! + simplex[1]![k]! + simplex[2]![k]!) / 3)
    const worst = simplex[3]!
    const at = (t: number) => centroid.map((c, k) => c + t * (worst[k]! - c))
    const reflected = at(-1)
    const fr = f(reflected)
    if (fr < values[0]!) {
      const expanded = at(-2)
      const fe = f(expanded)
      if (fe < fr) {
        simplex[3] = expanded
        values[3] = fe
      } else {
        simplex[3] = reflected
        values[3] = fr
      }
    } else if (fr < values[2]!) {
      simplex[3] = reflected
      values[3] = fr
    } else {
      const contracted = at(0.5)
      const fc = f(contracted)
      if (fc < values[3]!) {
        simplex[3] = contracted
        values[3] = fc
      } else {
        for (let i = 1; i < 4; i += 1) {
          simplex[i] = simplex[i]!.map((c, k) => simplex[0]![k]! + 0.5 * (c - simplex[0]![k]!))
          values[i] = f(simplex[i]!)
        }
      }
    }
  }
  const [v, h, r] = pack(simplex[0]!)
  const round = (n: number, d: number) => Math.round(n * 10 ** d) / 10 ** d
  return {
    geoVertical: Math.max(-100, Math.min(100, round(v, 0))),
    geoHorizontal: Math.max(-100, Math.min(100, round(h, 0))),
    geoRotate: Math.max(-45, Math.min(45, round(r, 1))),
  }
}
